import { useState, useRef, useEffect } from 'react';
import { usePlanActions } from '../../../hooks/usePlan';
import type { PlanData } from '../../../types/tabs';
import PlanStepCard from './PlanStepCard';

interface PlanTabProps {
  sessionId: string;
  planData?: PlanData;
  onExecuteNextStep?: () => void;
  onPlanUpdate?: (planData: PlanData) => void;
  onAddTask?: (description: string) => void;
  onSendChatMessage?: (message: string) => void;
}

export default function PlanTab({ sessionId, planData, onExecuteNextStep, onPlanUpdate, onAddTask, onSendChatMessage }: PlanTabProps) {
  const { isLoading, error, updatePlan } = usePlanActions({ sessionId, onPlanUpdate });
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [isRunningAll, setIsRunningAll] = useState(false);

  // Use ref to access latest planData in async function
  const planDataRef = useRef(planData);

  useEffect(() => {
    planDataRef.current = planData;
  }, [planData]);

  // Debug logging
  console.log('PlanTab received planData:', planData);
  console.log('PlanTab planData type:', typeof planData);
  console.log('PlanTab planData has ordered_tasks:', planData && 'ordered_tasks' in planData);

  const handleDeleteStep = async (stepIndex: number) => {
    if (!planData) return;

    setDeletingIndex(stepIndex);
    // Create new plan with task removed
    const updatedTasks = planData.ordered_tasks.filter((_, index) => index !== stepIndex);

    await updatePlan({ ...planData, ordered_tasks: updatedTasks });
    setDeletingIndex(null);
  };

  const handleUpdateDescription = async (stepIndex: number, newDescription: string) => {
    if (!planData) return false;

    const currentTask = planData.ordered_tasks[stepIndex];

    // If the task is awaiting user input, send a message through chat
    if (currentTask.status === 'awaiting_user' && onSendChatMessage) {
      onSendChatMessage(`Change current step to: ${newDescription}`);
      return true; // Don't update backend directly, let the chat handle it
    }

    // Create new plan with updated description (for non-awaiting_user tasks)
    const updatedTasks = planData.ordered_tasks.map((task, index) =>
      index === stepIndex ? { ...task, description: newDescription } : task
    );

    return await updatePlan({ ...planData, ordered_tasks: updatedTasks });
  };

  const handleExecuteNextStep = () => {
    if (onExecuteNextStep) {
      onExecuteNextStep();
    }
  };

  const handleAddTask = () => {
    if (newTaskDescription.trim() && onAddTask) {
      onAddTask(`Add to the plan: ${newTaskDescription.trim()}`);
      setNewTaskDescription('');
      setIsAddingTask(false);
    }
  };

  const handleCancelAddTask = () => {
    setNewTaskDescription('');
    setIsAddingTask(false);
  };

  const handleRunAllUntilUserInput = async () => {
    if (!onExecuteNextStep) return;

    setIsRunningAll(true);
    try {
      // Keep executing until we hit awaiting_user or no more pending steps
      while (true) {
        // Get latest planData from ref
        const currentPlanData = planDataRef.current;
        if (!currentPlanData) break;

        // Check if we have pending steps
        const currentHasPendingSteps = currentPlanData.ordered_tasks.some(
          t => t.status === 'pending' || t.status === 'in_progress'
        );
        if (!currentHasPendingSteps) break;

        // Check if current state has awaiting_user
        const hasAwaitingUser = currentPlanData.ordered_tasks.some(s => s.status === 'awaiting_user');
        if (hasAwaitingUser) break;

        // Execute next step
        await onExecuteNextStep();

        // Brief pause for user to see progress (1.5s per user preference)
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Note: planData will be updated by the streaming response
        // The loop will check the updated planData on next iteration
      }
    } catch (error) {
      console.error('Error during run all:', error);
    } finally {
      setIsRunningAll(false);
    }
  };

  // Empty state
  if (!planData || planData.ordered_tasks.length === 0) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="w-20 h-20 mx-auto mb-6 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Plan Yet</h2>
          <p className="text-gray-500 text-sm leading-relaxed">
            Start a conversation with the AI to create a plan for your task. Once a plan is created, you'll see the steps here.
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-6">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const completedSteps = planData.ordered_tasks.filter(t => t.status === 'completed').length;
  const totalSteps = planData.ordered_tasks.length;
  const progressPercent = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  const hasAwaitingUser = planData.ordered_tasks.some(t => t.status === 'awaiting_user');
  const hasPendingSteps = planData.ordered_tasks.some(t => t.status === 'pending' || t.status === 'in_progress');

  return (
    <div className="h-full w-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">Execution Plan</h1>
              <p className="text-sm text-gray-500">{completedSteps} of {totalSteps} steps completed</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Add Task Button */}
            <button
              onClick={() => setIsAddingTask(true)}
              disabled={isAddingTask}
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Add step"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
            </button>

            {/* Run All Until User Input Button */}
            <button
              onClick={handleRunAllUntilUserInput}
              disabled={!hasPendingSteps || isLoading || hasAwaitingUser || isRunningAll}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                hasPendingSteps && !isLoading && !hasAwaitingUser && !isRunningAll
                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
              title="Execute all steps until user input is needed"
            >
              {isRunningAll ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Running...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z M19 5v14l11-7z" />
                  </svg>
                  Run All
                </>
              )}
            </button>

            {/* Run Next Step Button */}
            <button
              onClick={handleExecuteNextStep}
              disabled={!hasPendingSteps || isLoading || hasAwaitingUser || isRunningAll}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                hasPendingSteps && !isLoading && !hasAwaitingUser && !isRunningAll
                  ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm hover:shadow-md'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
              Run Next Step
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-green-500 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Steps list */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {planData.ordered_tasks.map((task, index) => (
          <PlanStepCard
            key={index}
            step={{ ...task, index }}
            onDelete={() => handleDeleteStep(index)}
            isDeleting={deletingIndex === index}
            onUpdateDescription={handleUpdateDescription}
          />
        ))}

        {/* Add Task Input Card */}
        {isAddingTask && (
          <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 mb-3 bg-blue-50">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-7 h-7 rounded-full bg-blue-200 text-blue-600 flex items-center justify-center">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <span className="font-medium text-gray-700">New Step</span>
            </div>
            <textarea
              value={newTaskDescription}
              onChange={(e) => setNewTaskDescription(e.target.value)}
              placeholder="Describe what you'd like to add to the plan..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              rows={3}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleAddTask();
                }
                if (e.key === 'Escape') {
                  handleCancelAddTask();
                }
              }}
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={handleCancelAddTask}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddTask}
                disabled={!newTaskDescription.trim()}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add to Plan
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
